import React, { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { getWorkerPayments, listCashChanges } from "../../../../services/cashBookApi";
import { useKT } from "../../i18n/kt";
import { kp } from "../../karkhanaPaths";
import { fmtDay, rupees } from "../../rates/rateModel";
import PageHeader from "../../ui/PageHeader";
import CashbookGuard from "../CashbookGuard";
import type { ChangeItem, WorkerPaymentItem, WorkerPayments } from "../types";
import ChangeDecision from "./ChangeDecision";
import { myUserId, ownerRefusal, ownerStoryLine } from "./ownerText";
import { RowCrop } from "./PhotoBand";
import { bandOf } from "./sheetCheckModel";
import "../cashbook.scss";
import "./owner.scss";

/**
 * A pending change as the decision needs it. The changes list carries `canDecide`; when the list did not
 * answer, the worker card still knows who asked, so the requester is never offered the decision.
 */
export const changeFor = (item: WorkerPaymentItem, person: WorkerPayments["person"], changes: ChangeItem[], me: string): ChangeItem | null => {
  const p = item.pendingChange;
  if (!p) return null;
  const listed = changes.find((c) => c.proposalSeq === p.proposalSeq);
  if (listed) return listed;
  return {
    proposalSeq: p.proposalSeq,
    recordType: item.recordType,
    recordId: item.id,
    sheetNo: item.sheetNo,
    person,
    inForce: item.inForce,
    newAmount: p.newAmount,
    voidEntry: p.voidEntry,
    reasonCode: p.reasonCode as ChangeItem["reasonCode"],
    reason: p.reason,
    requestedBy: p.requestedBy,
    requestedAt: p.requestedAt,
    canDecide: p.requestedBy?.userId && p.requestedBy.userId === me ? "YOU_ASKED" : "OK",
    originalPhotoId: item.photoId,
    originalRowIndex: item.rowIndex,
  };
};

const PaymentItem = ({ item, person, changes, onDecided }: { item: WorkerPaymentItem; person: WorkerPayments["person"]; changes: ChangeItem[]; onDecided: () => void }) => {
  const { t } = useKT();
  // Keep the decision on screen after the reload clears the pending change ("₹200 is now in force").
  const [held, setHeld] = useState<ChangeItem | null>(null);
  const change = changeFor(item, person, changes, myUserId()) || held;
  const story = item.story.map((s) => ({ s, line: ownerStoryLine(s, t) })).filter((x) => x.line);
  const pending = item.pendingChange;
  return (
    <li className="cbo-pay">
      <div className="cbo-pay__meta">
        {[item.sheetNo ? `${item.sheetNo}${item.revision && item.revision > 1 ? ` · ${t("Print {{n}}", { n: item.revision })}` : ""}` : t("Paid in the app"), fmtDay(item.date)].join(" · ")}
      </div>
      <div className="cbo-pay__line">
        {pending ? (
          <>
            <s>{rupees(item.inForce)}</s>
            <strong>{pending.voidEntry || pending.newAmount === null ? t("remove") : rupees(pending.newAmount)}</strong>
            <span className="cbo-chip is-doubt">{t("change pending")}</span>
          </>
        ) : (
          <strong>{rupees(item.inForce)}</strong>
        )}
        <span className={`cbo-chip ${item.verifyState === "VERIFIED" ? "is-ok" : ""}`}>{item.verifyState === "VERIFIED" ? t("Verified") : t("Not checked yet")}</span>
      </div>
      {story.length ? (
        <ol className="cbo-story">
          {story.map(({ s, line }) => (
            <li key={s.seq}>{line}</li>
          ))}
        </ol>
      ) : null}
      {item.photoId && item.rowIndex ? (
        <RowCrop photoId={item.photoId} band={bandOf(item.rowIndex, item.page || 1)} label={t("Row {{n}}, {{name}}", { n: item.rowIndex, name: person.name })} />
      ) : null}
      {change ? (
        <ChangeDecision
          change={change}
          onDecided={() => {
            setHeld(change);
            onDecided();
          }}
        />
      ) : null}
    </li>
  );
};

const CardScreen = () => {
  const { t } = useKT();
  const { personId } = useParams<{ personId: string }>();
  const [data, setData] = useState<WorkerPayments | null>(null);
  const [changes, setChanges] = useState<ChangeItem[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [more, setMore] = useState(false);
  const [moreError, setMoreError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setError(null);
    try {
      const [res, ch] = await Promise.all([getWorkerPayments(personId), listCashChanges("PENDING").catch(() => null)]);
      if (res?.success && res.data) setData(res.data);
      else setError(ownerRefusal(res, t));
      setChanges(ch?.success ? ch.data?.changes || [] : []);
    } catch {
      setError(t("Couldn't load. Check the network."));
    }
  }, [personId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    load();
  }, [load]);

  const older = async () => {
    if (!data?.nextBefore) return;
    setMore(true);
    setMoreError(null);
    try {
      const res = await getWorkerPayments(personId, data.nextBefore);
      if (res?.success && res.data) setData({ ...data, items: [...data.items, ...res.data.items], nextBefore: res.data.nextBefore });
      else setMoreError(ownerRefusal(res, t));
    } catch {
      setMoreError(t("Couldn't load. Check the network."));
    } finally {
      setMore(false);
    }
  };

  const title = data ? `${data.person.name} · ${data.person.code}` : t("Worker payments");
  const header = <PageHeader title={title} back={kp.cashBook()} backLabel={t("Back")} />;

  if (error && !data)
    return (
      <div className="cb cbo">
        {header}
        <div className="cb-error" role="alert">
          <span>{error}</span>
          <button type="button" className="kh-btn" onClick={load}>
            {t("Retry")}
          </button>
        </div>
      </div>
    );
  if (!data)
    return (
      <div className="cb cbo" role="status" aria-label={t("Loading…")}>
        {header}
        <div className="cb-skeleton is-big" />
        <div className="cb-skeleton" />
      </div>
    );

  return (
    <div className="cb cbo" style={{ maxWidth: 760 }}>
      {header}
      {data.items.length ? (
        <ul className="cbo-rows">
          {data.items.map((item) => (
            <PaymentItem key={`${item.recordType}-${item.id}`} item={item} person={data.person} changes={changes} onDecided={load} />
          ))}
        </ul>
      ) : (
        <p className="cb-muted">{t("No cash book payments for {{name}} yet.", { name: data.person.name })}</p>
      )}
      {data.nextBefore ? (
        <button type="button" className="cb-btn is-small" onClick={older} disabled={more}>
          {more ? t("Loading…") : t("Older payments")}
        </button>
      ) : null}
      {moreError ? (
        <p className="cbo-row__err" role="alert">
          {moreError}
        </p>
      ) : null}
    </div>
  );
};

/** /karkhana/money/cash-book/worker/:personId — owners and managers only (D39, 7.15). */
const WorkerPaymentCard = () => (
  <CashbookGuard staffOnly>
    <CardScreen />
  </CashbookGuard>
);

export default WorkerPaymentCard;
